import { measurementsRows, useMeasurementsHistoryPresets, useMeasurementsTables } from "@api/generated/measurements/measurements";
import type { ColumnOut, FieldModeSettings, ReadingIn, TableOut } from "@api/generated/model";
import { useUserMe } from "@api/generated/user/user";
import { Icon } from "@app/icons";
import { useTrial } from "@app/trials";
import { hasRole, useSaveSetting, useSeasonFromLink, useSettings } from "@app/workspace";
import { enqueue, flush, plotOf, queued } from "@features/trials/fieldQueue";
import { parsePlotCode } from "@features/trials/plotkey";
import { donePlots, routeOrder } from "@features/trials/station";
import { TrialPicker, useTrialPlots } from "@features/trials/TrialPicker";
import { hint } from "@i18n";
import "@features/trials/station.css";
import { ActionIcon, Button, Drawer, MultiSelect, NumberInput, Progress, TextInput } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { cellText, shortTime } from "@ui/format";
import type { TFunction } from "i18next";
import { type ReactNode, useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const search = z.object({
  ev: z.number().int().optional(),
  kis: z.string().optional(),
  th: z.string().optional(),
  parc: z.number().int().optional(),
});

export const Route = createFileRoute("/field")({ validateSearch: search, component: FieldMode });

const KEEP = { staleTime: 10 * 60_000, gcTime: Number.POSITIVE_INFINITY, retry: 3 }; // the walk goes on offline
const SCALE = [1, 2, 3, 4, 5, 6, 7, 8, 9];

type Field = { id: string; table: string; column: string; label: string; kind: string };

const rowsOfTables = (results: { data?: { rows: Record<string, unknown>[] } }[]) => results.map((r) => r.data?.rows ?? []);

/** Whether the browser has a network (the online / offline events), for the sync status. */
function useOnline() {
  return useSyncExternalStore(
    (changed) => {
      window.addEventListener("online", changed);
      window.addEventListener("offline", changed);
      return () => {
        window.removeEventListener("online", changed);
        window.removeEventListener("offline", changed);
      };
    },
    () => navigator.onLine,
  );
}

/** The sync status in words: waiting saves (with or without a network), else everything sent (and when). */
function syncText(t: TFunction, waiting: number, online: boolean, sentAt: Date | null): string {
  if (waiting) return t(online ? "fieldMode.pending" : "fieldMode.offline", { count: waiting });
  return sentAt ? t("fieldMode.allSentAt", { time: shortTime(sentAt.toISOString()) }) : t("fieldMode.allSent");
}
type Row = Record<string, unknown>;

/** The chosen properties that are editable columns of a measurement table (a saved one may have been dropped). */
function scoredFields(ids: readonly string[], tables: readonly TableOut[]): Field[] {
  const editable = new Map<string, ColumnOut>(
    tables.filter((x) => !x.register).flatMap((x) => x.columns.filter((c) => c.editable).map((c) => [`${x.name}.${c.name}`, c] as const)),
  );
  return ids.flatMap((id) => {
    const c = editable.get(id);
    const [table = "", column = ""] = id.split(".");
    return c ? [{ id, table, column, label: c.label || column, kind: c.kind }] : [];
  });
}

/** Terepi felvétel: scoring on a phone, walking the field plot after plot (the legacy Polycorder / HT630 field
 *  loggers). The chosen properties (e.g. LR, LH) are scored 1–9 or typed; every save goes to a queue on the phone
 *  and on to the server when there is network, so the walk never waits for a signal. */
function FieldMode() {
  const { t } = useTranslation();
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const client = useQueryClient();
  const me = useUserMe().data;
  const user = me?.username ?? "";
  const canRecord = hasRole(me?.roles, "nemesito");
  const ev = useSeasonFromLink(params.ev);
  const mode = useSettings()?.field_mode;
  const tables = useMeasurementsTables({ query: { staleTime: Number.POSITIVE_INFINITY } }).data ?? [];
  const { trials, trial, th } = useTrial(ev, params.kis, params.th);
  const kis = trial?.kis;
  const [setup, setSetup] = useState(false);
  const [code, setCode] = useState("");
  const [waiting, setWaiting] = useState<ReadingIn[]>(() => queued(user));
  const [sentAt, setSentAt] = useState<Date | null>(null); // the last time the queue emptied
  const online = useOnline();

  const fields = scoredFields(mode?.fields ?? [], tables);
  const fieldTables = [...new Set(fields.map((f) => f.table))];
  const plots = useTrialPlots(ev, kis, canRecord);
  // the rows of the scored tables; ``combine`` keeps the result the same object until a table's rows change
  const loaded = useQueries({
    queries: fieldTables.map((name) => ({
      queryKey: ["field-rows", ev, name, th, kis],
      enabled: !!ev && !!kis && !!th && canRecord,
      ...KEEP,
      queryFn: () => measurementsRows(ev ?? 0, name, { th, kis, limit: 5000 }),
    })),
    combine: rowsOfTables,
  });
  const rowsOf = new Map(fieldTables.map((name, i) => [name, loaded[i] ?? []])); // (the compiler keeps it while loaded is)
  const route = useMemo(
    () => routeOrder([...new Set(plots.map((r) => r.parc as number))], mode?.route_width ?? 8),
    [plots, mode?.route_width],
  );
  const index = Math.max(0, params.parc !== undefined ? route.indexOf(params.parc) : 0);
  const parc = route[index];

  // send the queue now, when the network comes back, and every half minute
  const sync = useCallback(async () => {
    if (!user) return;
    try {
      const { results, left } = await flush(user);
      if (!results.length) return;
      setWaiting(left);
      if (!left.length) setSentAt(new Date());
      void client.invalidateQueries({ queryKey: ["field-rows"] }); // the server now holds what the queue showed
      const conflicts = results.filter((r) => r.status === "conflict").length;
      if (conflicts) notifications.show({ color: "yellow", message: t("fieldMode.conflict", { count: conflicts }) });
    } catch {
      // no network: it stays queued
    }
  }, [user, client, t]);
  useEffect(() => {
    void sync();
    const id = setInterval(() => void sync(), 30_000);
    window.addEventListener("online", sync);
    return () => {
      clearInterval(id);
      window.removeEventListener("online", sync);
    };
  }, [sync]);

  const go = (to: number | undefined) => to !== undefined && navigate({ search: (s) => ({ ...s, parc: to }), replace: true });
  const jump = () => {
    const key = parsePlotCode(code, true) ?? (/^\d+$/.test(code.trim()) ? { th, kis, parc: Number(code), alp: 0 } : null);
    if (!key || key.kis !== kis || !route.includes(key.parc)) {
      notifications.show({ color: "red", message: t("fieldMode.noSuchPlot", { code: code }) });
      return;
    }
    setCode("");
    navigate({ search: (s) => ({ ...s, th: key.th ?? s.th, parc: key.parc }), replace: true });
  };
  const save = (readings: ReadingIn[]) => {
    if (readings.length) setWaiting(enqueue(user, readings));
    void sync();
    go(route[index + 1]);
  };

  const plot = parc !== undefined ? plots.find((r) => r.parc === parc) : undefined;
  // plots with any chosen property scored (on the server, or waiting in the phone's queue)
  const scored = new Set<number>();
  for (const f of fields) for (const p of donePlots(rowsOf.get(f.table) ?? [], f.column)) scored.add(p);
  for (const w of waiting) if (w.kis === kis && w.th === th) scored.add(w.parc);
  return (
    <div className="field-mode">
      <header>
        <div className="hstack" style={{ gap: 10 }}>
          <Link to="/trials/data-input" search={{ kis, th }} aria-label={t("fieldMode.back")} className="field-back">
            <Icon name="back" size={22} />
          </Link>
          <div className="stack grow" style={{ gap: 0 }}>
            <b style={{ fontSize: "1rem" }}>{t("fieldMode.title")}</b>
            <span className="sm" style={{ opacity: 0.75 }}>
              {[ev, th, kis, fields.map((f) => f.label).join(", ")].filter(Boolean).join(" · ")}
            </span>
          </div>
          <ActionIcon variant="subtle" color="gray" c="inherit" size={44} onClick={() => setSetup(true)} aria-label={t("fieldMode.setup")}>
            <Icon name="sliders" size={20} />
          </ActionIcon>
        </div>
        <button
          type="button"
          className={`field-sync${waiting.length ? " waiting" : ""}`}
          onClick={() => void sync()}
          aria-live="polite"
          title={t("fieldMode.sendNow")}
        >
          <span className="field-sync-dot" />
          {syncText(t, waiting.length, online, sentAt)}
        </button>
        {kis && (
          <TextInput
            size="md"
            leftSection={<Icon name="barcode" size={20} />}
            placeholder={t("fieldMode.scan")}
            aria-label={t("fieldMode.codePlaceholder")}
            value={code}
            autoComplete="off"
            onChange={(e) => setCode(e.currentTarget.value)}
            onKeyDown={(e) => e.key === "Enter" && jump()}
          />
        )}
      </header>
      <main>
        {kis && th && route.length > 0 && (
          <div className="stack" style={{ gap: 4 }}>
            <div className="hstack sm" style={{ justifyContent: "space-between" }}>
              <span>{t("fieldMode.scoredPlots")}</span>
              <b className="mono">
                {scored.size} / {route.length}
              </b>
            </div>
            <Progress value={(100 * scored.size) / route.length} size="sm" aria-label={t("fieldMode.scoredPlots")} />
          </div>
        )}
        {!canRecord ? (
          <p className="mu">{t("shell.breederOnly")}</p>
        ) : !kis || !th ? (
          <div className="stack" style={{ gap: 12 }}>
            <p className="mu">{t("fieldMode.chooseTrialAndSite")}</p>
            <TrialPicker
              size="md"
              trials={trials}
              trial={trial}
              th={th}
              onChange={(v, site) => navigate({ search: { kis: v, th: site } })}
            />
          </div>
        ) : fields.length === 0 ? (
          <p className="mu">{t("fieldMode.noFields")}</p>
        ) : parc !== undefined && ev ? (
          <PlotScoring
            key={`${kis}|${th}|${parc}`}
            plot={{ ev, th, kis, parc }}
            name={plot?.g_name}
            fields={fields}
            rowOf={(table) => rowsOf.get(table)?.find((r) => r.parc === parc && !r.alp)}
            waiting={waiting}
            previous={route[index - 1]}
            next={route[index + 1]}
            position={[index + 1, route.length]}
            onBack={() => go(route[index - 1])}
            onSave={save}
          />
        ) : null}
      </main>
      <FieldSetup
        opened={setup}
        onClose={() => setSetup(false)}
        mode={mode}
        tables={tables}
        trialPicker={
          <TrialPicker size="md" trials={trials} trial={trial} th={th} onChange={(v, site) => navigate({ search: { kis: v, th: site } })} />
        }
      />
    </div>
  );
}

type ScoringProps = {
  plot: { ev: number; th: string; kis: string; parc: number };
  name: unknown;
  fields: Field[];
  rowOf: (table: string) => Row | undefined;
  waiting: ReadingIn[];
  previous: number | undefined;
  next: number | undefined;
  position: [number, number];
  onBack: () => void;
  onSave: (readings: ReadingIn[]) => void;
};

/** One plot: its line, the 1–9 rows (or inputs) of the scored properties, back and "save, next". */
function PlotScoring({ plot, name, fields, rowOf, waiting, previous, next, position, onBack, onSave }: ScoringProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  // what the phone shows: the server's row, with a score still waiting in the queue on top
  const queuedHere = new Map(waiting.filter((r) => plotOf(r) === plotOf({ ...plot, table: r.table })).map((r) => [r.table, r.values]));
  const shown = (f: Field) => (f.id in draft ? draft[f.id] : (queuedHere.get(f.table)?.[f.column] ?? rowOf(f.table)?.[f.column] ?? null));
  const first = rowOf(fields[0]?.table ?? "");
  const firstScale = fields.find((f) => f.kind === "integer")?.id; // the scale's meaning is said once

  const save = () => {
    const readings = [...new Set(fields.map((f) => f.table))].flatMap((table) => {
      const changed = fields.filter((f) => f.table === table && f.id in draft);
      if (!changed.length) return [];
      const server = rowOf(table);
      return [
        {
          key: crypto.randomUUID(),
          ...plot,
          alp: 0,
          table,
          values: Object.fromEntries(changed.map((f) => [f.column, draft[f.id] ?? null])),
          expected: Object.fromEntries(changed.map((f) => [f.column, server?.[f.column] ?? null])),
          measured_at: new Date().toISOString(),
        },
      ];
    });
    onSave(readings);
  };

  return (
    <>
      <section className="card pad stack" style={{ gap: 4, borderRadius: 12 }}>
        <div className="hstack" style={{ gap: 8, alignItems: "baseline" }}>
          <b className="mono" style={{ fontSize: "1.875rem", fontWeight: 700 }}>
            {plot.parc}
          </b>
          <span style={{ fontSize: "1rem", fontWeight: 600 }}>{cellText(name ?? first?.g_name)}</span>
        </div>
        {first?.g_prev ? <span className="mono sm mu">{t("fieldMode.previous", { previous: first.g_prev })}</span> : null}
      </section>
      {fields.map((f) => {
        const value = shown(f);
        const set = (v: unknown) => setDraft((d) => ({ ...d, [f.id]: v }));
        if (f.kind === "integer") {
          return (
            <section key={f.id} className="stack" style={{ gap: 8 }}>
              <div className="hstack" style={{ alignItems: "baseline", gap: 8 }}>
                <b className="grow" style={{ fontSize: "0.95rem" }}>
                  {f.label}
                </b>
                {f.id === firstScale && <span className="sm mu">{t("fieldMode.scale")}</span>}
              </div>
              <div className="score" role="radiogroup" aria-label={f.label}>
                {SCALE.map((n) => (
                  // biome-ignore lint/a11y/useSemanticElements: a 3 × 3 keypad of big touch targets, not a radio list
                  <button key={n} type="button" role="radio" aria-checked={value === n} onClick={() => set(n)}>
                    {n}
                  </button>
                ))}
              </div>
              {value != null && (
                <button type="button" className="link-button score-clear" onClick={() => set(null)}>
                  {t("fieldMode.clearScore", { field: f.label })}
                </button>
              )}
            </section>
          );
        }
        return f.kind === "number" ? (
          <NumberInput
            key={f.id}
            size="lg"
            label={f.label}
            inputMode="decimal"
            value={(value as number | null) ?? ""}
            onChange={(v) => set(v === "" ? null : v)}
          />
        ) : (
          <TextInput
            key={f.id}
            size="lg"
            label={f.label}
            value={(value as string | null) ?? ""}
            onChange={(e) => set(e.currentTarget.value || null)}
          />
        );
      })}
      <div className="grow" />
      <div className="hstack" style={{ gap: 10 }}>
        <Button size="lg" variant="default" style={{ flex: 1 }} disabled={previous === undefined} onClick={onBack}>
          ‹ {previous ?? ""}
        </Button>
        <Button size="lg" style={{ flex: 2 }} onClick={save}>
          {next !== undefined ? t("fieldMode.saveNext", { parc: next }) : t("fieldMode.save")}
        </Button>
      </div>
      <span className="sm mu" style={{ textAlign: "center" }}>
        {t("fieldMode.route", { position: position[0], total: position[1] })}
      </span>
    </>
  );
}

type SetupProps = {
  opened: boolean;
  onClose: () => void;
  mode: FieldModeSettings | undefined;
  tables: TableOut[];
  trialPicker: ReactNode;
};

/** The trial, the scored properties and the route width (the properties and width are the user's settings). */
function FieldSetup({ opened, onClose, mode, tables, trialPicker }: SetupProps) {
  const { t } = useTranslation();
  const saveSetting = useSaveSetting();
  const presets = useMeasurementsHistoryPresets({ query: { staleTime: Number.POSITIVE_INFINITY } }).data;
  const scorable = new Set(
    scoredFields(
      (presets?.fields ?? []).map((f) => f.id),
      tables,
    ).map((f) => f.id),
  );
  return (
    <Drawer opened={opened} onClose={onClose} position="bottom" size="auto" title={t("fieldMode.setup")}>
      <div className="stack" style={{ gap: 12, paddingBottom: 12 }}>
        {trialPicker}
        {mode && (
          <>
            <MultiSelect
              placeholder={hint("searchSelect")}
              size="md"
              label={t("fieldMode.fields")}
              description={t("fieldMode.fieldsHelp")}
              searchable
              maxValues={12}
              data={(presets?.fields ?? [])
                .filter((f) => scorable.has(f.id))
                .map((f) => ({ value: f.id, label: `${f.table_label} · ${f.label}` }))}
              value={mode.fields}
              onChange={(v) => saveSetting("field_mode", { ...mode, fields: v })}
            />
            <NumberInput
              size="md"
              label={t("fieldMode.plotsPerRow")}
              min={1}
              max={100}
              allowDecimal={false}
              defaultValue={mode.route_width}
              key={mode.route_width}
              onBlur={(e) => {
                const width = Number(e.currentTarget.value);
                if (width >= 1 && width <= 100 && width !== mode.route_width) saveSetting("field_mode", { ...mode, route_width: width });
              }}
            />
          </>
        )}
        <Button onClick={onClose}>{t("fieldMode.done")}</Button>
      </div>
    </Drawer>
  );
}
